import { createApiClient } from '../../../../../api/apiClient';
import { buildOrderRulesRequest, unwrapApiPayload } from './requestBuilder';
import {
  OrderRulesDashboardResponse,
  OrderRulesFilterParams,
  OrderRulesKpiMetric,
  OrderRulesSummaryKpiCard,
  OrderRulesSummaryResponse,
} from './types';

// ─── API: /api/order-rules/summary ───────────────────────────────────────────

export const fetchOrderRulesSummary = async (
  params: OrderRulesFilterParams,
  locationIds: string[],
): Promise<OrderRulesSummaryResponse> => {
  const api = createApiClient();
  const body = buildOrderRulesRequest(params, locationIds);
  const res = await api.post<OrderRulesSummaryResponse>('/order-rules/summary', body);
  return unwrapApiPayload<OrderRulesSummaryResponse>(res.data);
};

// ─── Normalizer: backend → legacy dashboard shape ────────────────────────────

const mapSummaryCardTone = (card: OrderRulesSummaryKpiCard): OrderRulesKpiMetric['tone'] => {
  if (card.isAlert) return 'danger';
  if (card.target != null && card.isAboveTarget === false) return 'danger';
  return 'default';
};

/**
 * Formats the Est. Cost Savings KPI value: >= $1MM shows millions ("$2.41MM"),
 * $1,000 to just under $1MM shows thousands ("$2.41M"), sign preserved for negatives.
 */
const formatCostSavingsValue = (rawValue: string): string => {
  const num = Number(String(rawValue).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(num)) return rawValue;
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}MM`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(2)}M`;
  return `${sign}$${abs.toFixed(2)}`;
};

export const normalizeSummaryResponse = (
  data: OrderRulesSummaryResponse,
): Partial<OrderRulesDashboardResponse> => {
  // TOTAL Turn On / Turn Off are the sums across the High, Medium and Low confidence levels.
  const summaryRows = (data.tableRows ?? data.summaryRows ?? []).map((row) => {
    const highTurnOn = Number(row.highTurnOn ?? 0);
    const highTurnOff = Number(row.highTurnOff ?? 0);
    const mediumTurnOn = Number(row.mediumTurnOn ?? 0);
    const mediumTurnOff = Number(row.mediumTurnOff ?? 0);
    const lowTurnOn = Number(row.lowTurnOn ?? 0);
    const lowTurnOff = Number(row.lowTurnOff ?? 0);
    return {
      region: row.geographyName ?? row.region ?? 'N/A',
      highTurnOn,
      highTurnOff,
      mediumTurnOn,
      mediumTurnOff,
      lowTurnOn,
      lowTurnOff,
      totalTurnOn: highTurnOn + mediumTurnOn + lowTurnOn,
      totalTurnOff: highTurnOff + mediumTurnOff + lowTurnOff,
      isGrandTotal: false,
    };
  });

  const grandTotalRow = data.grandTotal ?? {
    geographyName: 'GRAND TOTAL',
    region: 'GRAND TOTAL',
    highTurnOn: 0,
    highTurnOff: 0,
    mediumTurnOn: 0,
    mediumTurnOff: 0,
    lowTurnOn: 0,
    lowTurnOff: 0,
    totalTurnOn: 0,
    totalTurnOff: 0,
    isGrandTotal: true,
  };

  const gtHighTurnOn = Number(grandTotalRow.highTurnOn ?? 0);
  const gtHighTurnOff = Number(grandTotalRow.highTurnOff ?? 0);
  const gtMediumTurnOn = Number(grandTotalRow.mediumTurnOn ?? 0);
  const gtMediumTurnOff = Number(grandTotalRow.mediumTurnOff ?? 0);
  const gtLowTurnOn = Number(grandTotalRow.lowTurnOn ?? 0);
  const gtLowTurnOff = Number(grandTotalRow.lowTurnOff ?? 0);
  const normalizedGrandTotal = {
    region: grandTotalRow.geographyName ?? grandTotalRow.region ?? 'GRAND TOTAL',
    highTurnOn: gtHighTurnOn,
    highTurnOff: gtHighTurnOff,
    mediumTurnOn: gtMediumTurnOn,
    mediumTurnOff: gtMediumTurnOff,
    lowTurnOn: gtLowTurnOn,
    lowTurnOff: gtLowTurnOff,
    totalTurnOn: gtHighTurnOn + gtMediumTurnOn + gtLowTurnOn,
    totalTurnOff: gtHighTurnOff + gtMediumTurnOff + gtLowTurnOff,
    isGrandTotal: true,
  };

  return {
    lastRefreshedAt: data.generatedAt ?? data.lastRefreshedAt ?? '',
    recommendationWindowLabel: data.recommendationWindowLabel,
    kpis: (data.kpiCards ?? []).map((card, index) => {
      const id = ['sku-on', 'sku-off', 'avg-enrollment', 'savings', 'check-flags'][index] ?? `kpi-${index + 1}`;
      const isSavings = id === 'savings';
      return {
        id,
        label: card.title ?? `KPI ${index + 1}`,
        value: isSavings ? formatCostSavingsValue(card.value ?? '0') : card.value ?? '0',
        subLabel: card.subCaption ?? card.hint ?? '',
        // Est. Cost Savings is never target-colored, regardless of what the API returns.
        tone: isSavings ? 'default' : mapSummaryCardTone(card),
      };
    }),
    summaryDrillLevel: data.drillLevel,
    skuRecommendationSummary: [normalizedGrandTotal, ...summaryRows],
  };
};
